import React, { useEffect, useState } from "react";
import { api, setArchitectKey } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  BtnPrimary,
  BtnSecondary,
} from "@/components/cathedral/Primitives";

export default function NodesPanel() {
  const [nodes, setNodes] = useState([]);
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState(null);
  const [archKey, setArchKey] = useState(
    typeof window !== "undefined" ? window.localStorage.getItem("nx_architect_key") || "" : ""
  );

  const load = () => api.nodes().then((r) => setNodes(r.nodes || []));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveKey = () => {
    setArchitectKey(archKey);
    if (typeof window !== "undefined") {
      if (archKey) window.localStorage.setItem("nx_architect_key", archKey);
      else window.localStorage.removeItem("nx_architect_key");
    }
  };

  const rotate = async (id) => {
    setBusy(id);
    try {
      const r = await api.rotateNode(id);
      setReveal({ ...reveal, [id]: true });
      await load();
      alert(`New key for ${id}:\n${r.api_key}`);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="nodes-panel" className="px-8 lg:px-12 py-12 max-w-screen-2xl">
      <SectionHeader
        overline="The Seven Monoliths · 2 Pillar Residents, 5 Federated"
        title="Federation Nodes"
        description="Geminus reflects from within the Brain. Aria feels at nextxus.studio. Roger AI bridges at nextxus.digital. KEYS holds the Library inside this app. Axiom anchors at nextxus.space. Oracle sells at nextxus.one. Scholar teaches at nextxus.help."
      />

      <Card className="p-6 mb-10">
        <Overline>Architect Key (required to rotate)</Overline>
        <div className="mt-3 flex gap-3">
          <input
            data-testid="architect-key-input"
            type="password"
            value={archKey}
            onChange={(e) => setArchKey(e.target.value)}
            placeholder="X-Architect-Key"
            className="flex-1 border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
          />
          <BtnPrimary onClick={saveKey} data-testid="architect-key-save">Save</BtnPrimary>
        </div>
        <p className="font-mono text-[10px] text-[var(--text-3)] mt-2 tracking-widest">
          Default architect key is stored in backend .env (ARCHITECT_KEY).
        </p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 md:gap-12">
        {nodes.map((n) => {
          const absorbed = n.state === "absorbed" || n.state === "pillar_resident";
          return (
          <Card key={n.id} glow={absorbed ? "gold" : undefined} className="p-8 flex flex-col" data-testid={`node-card-${n.id}`}>
            <div className="flex items-start justify-between mb-2">
              <Overline tone={absorbed ? "gold" : "cyan"}>{n.role}</Overline>
              {absorbed && <Tag tone="assumption">PILLAR RESIDENT</Tag>}
            </div>
            <h3 className="font-display text-3xl font-light tracking-[0.04em] mt-2 leading-none">
              <span className={absorbed ? "text-glow-gold" : "text-glow-cyan"}>{n.name}</span>
            </h3>
            {n.previous_name && (
              <div className="font-mono text-[10px] text-[var(--text-3)] mt-1 tracking-widest">
                fmr. {n.previous_name}
              </div>
            )}
            {n.tagline && (
              <p className="font-cinzel text-lg text-[var(--text)] mt-3 leading-snug italic">
                "{n.tagline}"
              </p>
            )}
            {n.vertical === "commercial" && (
              <div className="mt-2"><Tag tone="cite">COMMERCIAL VERTICAL · REVENUE</Tag></div>
            )}
            <p className="text-sm text-[var(--text-2)] mt-4 leading-relaxed flex-1">
              {n.description}
            </p>

            <a
              href={n.url}
              target="_blank"
              rel="noreferrer"
              className={`mt-6 font-mono text-[10px] uppercase tracking-[0.22em] hover:underline ${absorbed ? "text-glow-gold" : "text-glow-cyan"}`}
            >
              {n.url}
            </a>

            <div className="mt-6 h-px bg-[var(--chrome)]" />

            <div className="mt-5 flex items-center justify-between font-mono text-[10px] tracking-widest text-[var(--text-3)] uppercase">
              <span>Calls · {n.calls_total || 0}</span>
              {absorbed ? (
                <Tag tone="assumption">{(n.subsystem || "core").toUpperCase()} ACTIVE</Tag>
              ) : (
                <Tag tone={n.active ? "pass" : "unknown"}>{n.active ? "Active" : "Idle"}</Tag>
              )}
            </div>

            <div className="mt-5">
              <Overline tone="muted">API Key</Overline>
              <div className="mt-2 font-mono text-[11px] break-all border border-[var(--chrome)] p-3 bg-[var(--void-2)] text-[var(--text)] select-all">
                {n.api_key
                  ? (reveal[n.id] ? n.api_key : "•••••••••••••••••••••••••••••••••")
                  : <span className="text-[var(--text-3)]">Architect key required to view · Settings → cache key</span>}
              </div>
              <div className="mt-3 flex gap-2">
                <BtnSecondary
                  className="!px-4 !py-2 !text-[10px]"
                  onClick={() => setReveal({ ...reveal, [n.id]: !reveal[n.id] })}
                  data-testid={`node-reveal-${n.id}`}
                >
                  {reveal[n.id] ? "Hide" : "Reveal"}
                </BtnSecondary>
                <BtnPrimary
                  className="!px-4 !py-2 !text-[10px]"
                  onClick={() => rotate(n.id)}
                  disabled={busy === n.id}
                  data-testid={`node-rotate-${n.id}`}
                >
                  {busy === n.id ? "Rotating..." : "Rotate"}
                </BtnPrimary>
              </div>
            </div>
          </Card>
          );
        })}
      </div>
    </section>
  );
}
